import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";
import { sortBySelectors } from "@lyfegen-platform/utils";
import { isEmptyArray, useFieldValue } from "formik";
import * as Yup from "yup";
import { FlexControlProps } from "../models/flexible/flex-field-controller";
import { getAssessmentDate, returnFirstResult } from "../utils/agreement-utils";
import { distinctBy } from "../utils/distinct-by";
import { getSectionDataWithAttribute } from "../utils/flex-entity-utils";
import { isEmptyValue } from "../utils/is-empty-value";
import { renderRequiredMessage } from "../utils/render-required-message";
import { GeneralFieldController } from "./general-field-controller";

export class GeneralTreatingPhysicianFieldController extends GeneralFieldController {
	override createValidationRule(initialData: unknown, translation: (key: string) => string): Yup.AnySchema {
		const adverseEventsSection = this.parent.context.schema.sections.find((section) =>
			section.fields.some((field) => field.attribute.identifier === AttributeIdentifiers.AdverseEvent)
		);

		return !!adverseEventsSection
			? this.createFieldValidationRuleByDataType(translation).when("$values", (values: any, schema: Yup.AnySchema) =>
					!isEmptyArray(values[adverseEventsSection.localizableKey])
						? schema.required(renderRequiredMessage(this.schema.attribute.identifier, translation))
						: schema
			  )
			: super.createValidationRule(initialData, translation);
	}

	override useControlProps(): FlexControlProps {
		const adverseEventsSection = this.parent.context.sectionControllers.find((controller) =>
			controller.fieldControllers.some((field) => field.schema.attribute.identifier === AttributeIdentifiers.AdverseEvent)
		);
		if (!adverseEventsSection) {
			return null;
		}

		const [adverseEventsValues] = useFieldValue(adverseEventsSection.path);
		return (adverseEventsValues || []).length
			? {
					required: true,
			  }
			: {};
	}

	override mapInitialValue(value: unknown): unknown {
		const flexEntities = distinctBy(
			this.parent.context.cases.flatMap((cs) => cs.flexEntities),
			(entity) => entity.id
		);
		if (value !== undefined || flexEntities.length === 0) {
			return isEmptyValue(value) ? "" : value;
		}

		const flexEntitiesWithDates = flexEntities
			.map((entity) => ({
				entity,
				date: getAssessmentDate(entity),
			}))
			.filter((tuple) => !!tuple.date)
			.sort(sortBySelectors((tuple) => tuple.date));
		const container =
			returnFirstResult(flexEntitiesWithDates, (tuple) =>
				getSectionDataWithAttribute<any>(tuple.entity.data, AttributeIdentifiers.TreatingPhysicianTitle)
			) || {};

		return container[this.schema.attribute.identifier] || "";
	}
}
